// Opt-in sharing: Web Share API with a clipboard fallback, and a client-side
// PNG result-card renderer. No imports — this file is used both bundled
// (funnel/archetype JS) and unbundled as a raw <script type="module"> on the
// generated static share pages (archetypes/*.html, loops/*.html).

/**
 * Share a result via the native share sheet when available (attaching cardBlob
 * as a file if the platform supports file-sharing), otherwise copy the URL to
 * the clipboard. Never throws — callers use the return value to update UI
 * (e.g. "Link copied ✓" for 2s on 'copied').
 * @returns {Promise<'shared'|'copied'|'unsupported'>}
 */
export async function shareResult({ title, text, url, cardBlob } = {}) {
  const canWebShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';
  const canClipboard =
    typeof navigator !== 'undefined' && navigator.clipboard && typeof navigator.clipboard.writeText === 'function';

  if (!canWebShare && !canClipboard) return 'unsupported';

  if (canWebShare) {
    let payload = {};
    if (title) payload.title = title;
    if (text) payload.text = text;
    if (url) payload.url = url;

    if (cardBlob && typeof File === 'function' && typeof navigator.canShare === 'function') {
      const file = new File([cardBlob], 'mindlift-result.png', { type: cardBlob.type || 'image/png' });
      const withFile = Object.assign({}, payload, { files: [file] });
      if (navigator.canShare(withFile)) payload = withFile;
    }

    try {
      await navigator.share(payload);
      return 'shared';
    } catch (err) {
      // AbortError = the person closed the native share sheet without picking anything.
      // That is a completed (if empty-handed) share flow, not a failure — do not fall
      // through to the clipboard fallback and silently copy a link they didn't ask for.
      if (err && err.name === 'AbortError') return 'shared';
      // Any other error (permissions, unsupported payload on this platform, etc.)
      // falls through to the clipboard fallback below.
    }
  }

  if (canClipboard && url) {
    try {
      await navigator.clipboard.writeText(url);
      return 'copied';
    } catch (err) {
      return 'unsupported';
    }
  }

  return 'unsupported';
}

// Kept in sync by hand with the identical ILLO_VARS map in scripts/build-share-images.mjs
// (that script substitutes for sharp/librsvg, which also has no CSS custom-property support;
// this one substitutes for an <img>-loaded SVG, which likewise can't see the page's --illo-*
// custom properties defined in css/funnel.css — both need the same 4 literal hex values).
const ILLO_VARS = {
  '--illo-ochre': '#E2B86B',
  '--illo-sage': '#697162',
  '--illo-blush': '#F2A897',
  '--illo-rose': '#DAB4AD'
};

function substituteIlloVars(svgString) {
  let out = svgString;
  for (const name in ILLO_VARS) {
    out = out.split('var(' + name + ')').join(ILLO_VARS[name]);
  }
  return out;
}

/**
 * Render an SVG template string (with placeholders already substituted by the
 * caller) to a 1080x1350 PNG Blob via an offscreen <canvas>. Used for the
 * downloadable/attachable result card. Requires a DOM (browser only).
 * @param {string} svgString
 * @returns {Promise<Blob>}
 */
export function renderCard(svgString) {
  return new Promise((resolve, reject) => {
    if (typeof document === 'undefined') {
      reject(new Error('renderCard requires a DOM'));
      return;
    }
    const W = 1080;
    const H = 1350;
    const svgBlob = new Blob([substituteIlloVars(svgString)], { type: 'image/svg+xml;charset=utf-8' });
    const objectUrl = URL.createObjectURL(svgBlob);
    const img = new Image();
    img.onload = function () {
      const canvas = document.createElement('canvas');
      canvas.width = W;
      canvas.height = H;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, W, H);
      URL.revokeObjectURL(objectUrl);
      canvas.toBlob(function (blob) {
        if (blob) resolve(blob);
        else reject(new Error('renderCard: canvas.toBlob returned null'));
      }, 'image/png');
    };
    img.onerror = function () {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('renderCard: failed to load svg into <img>'));
    };
    img.src = objectUrl;
  });
}

/**
 * Build an absolute mindlifthere.com URL, stripping empty/null/undefined params.
 * @param {string} base - path, with or without a leading slash
 * @param {Record<string,string|undefined|null>} params
 * @returns {string}
 */
export function buildShareUrl(base, params) {
  const ORIGIN = 'https://mindlifthere.com';
  let path = String(base || '/');
  if (path.charAt(0) !== '/') path = '/' + path;
  const qs = [];
  const p = params || {};
  Object.keys(p).forEach(function (k) {
    const v = p[k];
    if (v === undefined || v === null || v === '') return;
    qs.push(encodeURIComponent(k) + '=' + encodeURIComponent(v));
  });
  return ORIGIN + path + (qs.length ? '?' + qs.join('&') : '');
}

/** Flashes "Link copied ✓" on a button for `ms` (default 2000ms), then restores its original text. Pairs with shareResult()'s 'copied' result. */
export function flashCopied(btn, ms) {
  if (!btn) return;
  const orig = btn.textContent;
  btn.textContent = 'Link copied ✓';
  setTimeout(function () {
    btn.textContent = orig;
  }, ms || 2000);
}
